import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './TaskTable.css';
import MDTasksTable from './Components/TaskData/MDTaskTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faCog,
  faTasks,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import ContactsPage from './Components/ContactsPage/ContactsPage';
import MatterCustomFields from './Components/CustomFields/MatterCustomFields';
import ChecklistList from './Components/ChecklistList/ChecklistList';
import ProjectChecklistTable from '../../../ProjectTasks/ProjectChecklist/Components/ChecklistTable/ChecklistTable';
import { GetMatterByIdResponse } from '../../../../../Models/Matter/MatterRequestResponseModels';
import { isEngineeringEnvironment } from '../../../../../AppSettings/environmentConfig';
import TaskService from '../../../../../AppSettings/services/TaskService';
import MatterService from '../../../../../AppSettings/services/MatterService';
import { TaskByEmployee } from '../../../../../Models/Tasks/TaskModels';

interface TaskTableProps {
  matterData?: GetMatterByIdResponse;
}

const TaskTable: React.FC<TaskTableProps> = ({ matterData }) => {
  const { matterId } = useParams<{ matterId: string }>();
  // Default to Tasks (table) for both environments
  const [view, setView] = useState<string>('table');
  const [tasksCount, setTasksCount] = useState<number>(0);
  const [contactsCount, setContactsCount] = useState<number>(0);

  // Fetch tasks count
  const fetchTasksCount = useCallback(async () => {
    if (!matterId) return;
    try {
      const tasks: TaskByEmployee[] = await TaskService.getTasksByMatterId(matterId);
      setTasksCount(tasks.length);
    } catch (error) {
      console.error('Error fetching tasks count:', error);
    }
  }, [matterId]);

  // Fetch contacts count (same logic as ContactsPage: leader + associated with non-null employeeid)
  const fetchContactsCount = useCallback(async () => {
    if (!matterId) return;
    try {
      const matter = await MatterService.getMatterDetails(matterId);
      const leaderCount = matter.employee_details && matter.employeeid ? 1 : 0;
      const associatedCount =
        matter.associated_employees?.filter((emp) => emp.employeeid != null).length ?? 0;
      setContactsCount(leaderCount + associatedCount);
    } catch (error) {
      console.error('Error fetching contacts count:', error);
    }
  }, [matterId]);

  useEffect(() => {
    fetchTasksCount();
  }, [fetchTasksCount]);

  useEffect(() => {
    fetchContactsCount();
  }, [fetchContactsCount]);

  useEffect(() => {
    if (view === 'contacts') {
      fetchContactsCount();
    }
  }, [view, fetchContactsCount]);

  const toggleView = (selectedView: string): void => {
    // Prevent switching to Custom Fields in engineering environment
    if (isEngineeringEnvironment && selectedView === 'custom-fields') {
      return;
    }
    setView(selectedView);
  };

  return (
    <div className="matter-data-tasks-page">
      <div className="task-header">
        <div className="task-tabs-container">
          <div className="task-tabs-wrapper">
            <div className="task-tabs">
              {/* Engineering: Tasks, Checklists, Contacts | Legal: Tasks, Checklist, Contacts, Custom Fields */}
              {isEngineeringEnvironment ? (
                <>
                  {/* Engineering: Tasks first */}
                  <button
                    className={`task-tab ${view === 'table' ? 'active' : ''}`}
                    onClick={() => toggleView('table')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faTasks} />
                      </span>
                      <span>Tasks</span>
                    </span>
                    {tasksCount > 0 && (
                      <span className="task-tab-count">{tasksCount}</span>
                    )}
                  </button>
                  {/* Engineering: Checklist second */}
                  <button
                    className={`task-tab ${view === 'checklist' ? 'active' : ''}`}
                    onClick={() => toggleView('checklist')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faList} />
                      </span>
                      <span>Checklist</span>
                    </span>
                  </button>
                  {/* Engineering: Contacts third */}
                  <button
                    className={`task-tab ${view === 'contacts' ? 'active' : ''}`}
                    onClick={() => toggleView('contacts')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <span>Contacts</span>
                    </span>
                    {contactsCount > 0 && (
                      <span className="task-tab-count">{contactsCount}</span>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* Legal: Tasks first */}
                  <button
                    className={`task-tab ${view === 'table' ? 'active' : ''}`}
                    onClick={() => toggleView('table')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faTasks} />
                      </span>
                      <span>Tasks</span>
                    </span>
                    {tasksCount > 0 && (
                      <span className="task-tab-count">{tasksCount}</span>
                    )}
                  </button>
                  {/* Legal: Checklist second */}
                  <button
                    className={`task-tab ${view === 'checklist' ? 'active' : ''}`}
                    onClick={() => toggleView('checklist')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faList} />
                      </span>
                      <span>Checklist</span>
                    </span>
                  </button>
                  {/* Legal: Contacts fourth */}
                  <button
                    className={`task-tab ${view === 'contacts' ? 'active' : ''}`}
                    onClick={() => toggleView('contacts')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <span>Contacts</span>
                    </span>
                    {contactsCount > 0 && (
                      <span className="task-tab-count">{contactsCount}</span>
                    )}
                  </button>
                  {/* Legal: Custom Fields fifth */}
                  <button
                    className={`task-tab ${
                      view === 'custom-fields' ? 'active' : ''
                    }`}
                    onClick={() => toggleView('custom-fields')}
                  >
                    <span className="task-tab-content">
                      <span className="task-tab-icon">
                        <FontAwesomeIcon icon={faCog} />
                      </span>
                      <span>Custom Fields</span>
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div className="task-total-count">2</div>
        </div> */}
      </div>
      <div className="matter-data-task-content-container">
        {view === 'table' ? (
          <MDTasksTable />
        ) : view === 'checklist' ? (
          // Use custom ProjectChecklist table for engineering environment, regular table for legal
          isEngineeringEnvironment ? (
            <ProjectChecklistTable />
          ) : (
            <ChecklistList />
          )
        ) : view === 'contacts' ? (
          <ContactsPage />
        ) : (
          <MatterCustomFields />
        )}
      </div>
    </div>
  );
};

export default TaskTable;
